import React from 'react';
import {
  useFormContext,
  useWatch,
  type FieldPath,
  type FieldPathValue,
} from 'react-hook-form';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { getStringValue } from '~/modules/guarantees/utils.ts';
import { GuaranteeSecurityType, type TGuaranteesAccount } from '~/store/guarantees/types.ts';
import {
  filterGuaranteesAccountOptionsBySecurityType,
  getGuaranteesAccountFieldBySecurityType,
  getGuaranteesCounterpartyAccountOptions,
  getGuaranteesPrincipalDebitAccountOptions,
} from '../../../rootSection/accountUtils.ts';
import {
  getComparableFormValue,
  getSelectedGuaranteeAccountSecurityType,
  getSelectedGuaranteeSecurityType,
  saveGuaranteesStoredAccountSelection,
  validateGuaranteesSecretSectionForm,
} from '../../../rootSection/utils.ts';
import {
  GuaranteesAccountTargetField,
  type TGuaranteesAccountOption,
  type TGuaranteesAccountSecurityType,
  type TGuaranteesRootSectionFormValues,
  type TGuaranteesSecretSectionSaveContext,
} from '../../../rootSection/types.ts';
import { findGuaranteesOptionByValue } from '../../../utils/options/utils.ts';
import {
  areSameAccountOptions,
  isGuaranteesAccountOption,
  mergeSelectedAccountOption,
} from '../selectionUtils.ts';

const SECRET_FIELD_NAMES = [
  'securityType',
  'isCounterpartyAccount',
  'isCounterpartyPrincipalDebitAccount',
  'securityAmount',
  'securityCurrencies',
  'requiredSecurityAmount',
  'pledgorName',
  'pledgorHasChangesInConstituentDocuments',
  'pledgorIdentifyCode',
  'principalDebitAccount',
  'principalDebitAccountIban',
  'account',
  'coverageAccountIban',
  'depositAccountIban',
] as const satisfies readonly (keyof TGuaranteesRootSectionFormValues)[];

type TSecretFieldName = (typeof SECRET_FIELD_NAMES)[number];
type TSecretSectionSnapshot = Partial<Record<TSecretFieldName, unknown>>;

const normalizeSecretSnapshotValue = (value: unknown): unknown => {
  if (value === undefined) {
    return null;
  }

  if (Array.isArray(value)) {
    return value.map(normalizeSecretSnapshotValue);
  }

  if (value !== null && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((snapshotValue, key) => {
        snapshotValue[key] = normalizeSecretSnapshotValue((value as Record<string, unknown>)[key]);

        return snapshotValue;
      }, {});
  }

  return value;
};

const getSecretSectionSnapshotSignature = (snapshot: TSecretSectionSnapshot): string =>
  JSON.stringify(normalizeSecretSnapshotValue(snapshot));

const buildSecretSectionSnapshot = (
  values: TGuaranteesRootSectionFormValues
): TSecretSectionSnapshot =>
  SECRET_FIELD_NAMES.reduce<TSecretSectionSnapshot>(
    (snapshot, fieldName) => ({
      ...snapshot,
      [fieldName]: values[fieldName],
    }),
    {}
  );

type TUseRootAccountSectionParams = {
  accounts: TGuaranteesAccount[];
  applicationLoadKey: string;
  guaranteeCurrencyOptions: TAutocompleteOption[];
  isLoadingAccounts: boolean;
  isLoadingCurrencies: boolean;
  isLoadingPrincipalDebitAccounts: boolean;
  isLoadingSecurityCurrencies: boolean;
  isSavedContractSelected: boolean;
  isSavedGuaranteeTypeSelected: boolean;
  isSavingAccount: boolean;
  lockedUntilSavedHint: string;
  principalDebitAccounts: TGuaranteesAccount[];
  onAccountsLoad: (params: { isPrincipalDebitAccount: boolean }) => Promise<void>;
  onPrincipalDebitAccountsLoad: () => Promise<void>;
  onSecurityCurrenciesLoad: () => Promise<void>;
  onSecretSubmit: (
    values: TGuaranteesRootSectionFormValues,
    context: TGuaranteesSecretSectionSaveContext
  ) => Promise<boolean>;
};

export const useRootAccountSection = ({
  accounts,
  applicationLoadKey,
  guaranteeCurrencyOptions,
  isLoadingAccounts,
  isLoadingCurrencies,
  isLoadingPrincipalDebitAccounts,
  isLoadingSecurityCurrencies,
  isSavedContractSelected,
  isSavedGuaranteeTypeSelected,
  isSavingAccount,
  lockedUntilSavedHint,
  principalDebitAccounts,
  onAccountsLoad,
  onPrincipalDebitAccountsLoad,
  onSecurityCurrenciesLoad,
  onSecretSubmit,
}: TUseRootAccountSectionParams) => {
  const {
    control,
    formState: { dirtyFields },
    clearErrors,
    getValues,
    resetField,
    setError,
    setValue,
  } = useFormContext<TGuaranteesRootSectionFormValues>();
  const selectedAccount = useWatch({
    control,
    name: 'account',
  });
  const isCounterpartyAccount = useWatch({
    control,
    name: 'isCounterpartyAccount',
  });
  const principalDebitAccount = useWatch({
    control,
    name: 'principalDebitAccount',
  });
  const isCounterpartyPrincipalDebitAccount = useWatch({
    control,
    name: 'isCounterpartyPrincipalDebitAccount',
  });
  const selectedSecurityTypeOption = useWatch({
    control,
    name: 'securityType',
  });
  const selectedSecurityType = getSelectedGuaranteeSecurityType(selectedSecurityTypeOption);
  const selectedAccountSecurityType =
    getSelectedGuaranteeAccountSecurityType(selectedSecurityTypeOption);
  const selectedAccountOption = isGuaranteesAccountOption(selectedAccount) ? selectedAccount : null;
  const selectedPrincipalDebitAccountOption = isGuaranteesAccountOption(principalDebitAccount)
    ? principalDebitAccount
    : null;
  const baseAccountOptions = React.useMemo(
    () => getGuaranteesCounterpartyAccountOptions(accounts),
    [accounts]
  );
  const principalDebitAccountOptions = React.useMemo(
    () => getGuaranteesPrincipalDebitAccountOptions(principalDebitAccounts),
    [principalDebitAccounts]
  );
  const accountOptions = React.useMemo(
    () => mergeSelectedAccountOption(baseAccountOptions, selectedAccountOption),
    [baseAccountOptions, selectedAccountOption]
  );
  const filteredAccountOptions = React.useMemo(
    () => filterGuaranteesAccountOptionsBySecurityType(accountOptions, selectedAccountSecurityType),
    [accountOptions, selectedAccountSecurityType]
  );
  const manualAccountFieldName = getGuaranteesAccountFieldBySecurityType(
    selectedAccountSecurityType
  );
  const loadedAccountsRef = React.useRef('');
  const loadedPrincipalDebitAccountsRef = React.useRef('');
  const loadedSecurityCurrenciesRef = React.useRef('');
  const previousSecurityTypeRef = React.useRef<TGuaranteesAccountSecurityType | null | undefined>(
    undefined
  );
  const draftAccountsBySecurityTypeRef = React.useRef<
    Partial<Record<TGuaranteesAccountSecurityType, TGuaranteesAccountOption>>
  >({});
  const isSecurityTypeSelected = !!selectedSecurityType;
  const accountLockedHint =
    !isSavedGuaranteeTypeSelected
      ? lockedUntilSavedHint
      : !isSavedContractSelected
        ? guaranteesClientText.contractRequiredHint
        : !selectedSecurityType
          ? guaranteesClientText.securityTypeRequiredHint
          : lockedUntilSavedHint;
  const watchedSecretFieldValues = useWatch({
    control,
    name: SECRET_FIELD_NAMES,
  });
  const pledgorIdentifyCode = useWatch({
    control,
    name: 'pledgorIdentifyCode',
  });
  const currentSecretSectionSnapshotSignature = React.useMemo(
    () =>
      getSecretSectionSnapshotSignature(
        SECRET_FIELD_NAMES.reduce<TSecretSectionSnapshot>((snapshot, fieldName, index) => {
          snapshot[fieldName] = watchedSecretFieldValues[index];

          return snapshot;
        }, {})
      ),
    [watchedSecretFieldValues]
  );
  const [savedSecretSectionSnapshotSignature, setSavedSecretSectionSnapshotSignature] =
    React.useState(currentSecretSectionSnapshotSignature);
  const savedPledgorIdentifyCodeRef = React.useRef(getStringValue(pledgorIdentifyCode));
  const hasDirtySecretFields = Boolean(
    dirtyFields.securityType ||
      dirtyFields.isCounterpartyAccount ||
      dirtyFields.isCounterpartyPrincipalDebitAccount ||
      dirtyFields.securityAmount ||
      dirtyFields.securityCurrencies ||
      dirtyFields.requiredSecurityAmount ||
      dirtyFields.pledgorName ||
      dirtyFields.pledgorHasChangesInConstituentDocuments ||
      dirtyFields.pledgorIdentifyCode ||
      dirtyFields.principalDebitAccount ||
      dirtyFields.principalDebitAccountIban ||
      dirtyFields.account ||
      dirtyFields.coverageAccountIban ||
      dirtyFields.depositAccountIban
  );
  const hasAccountChanges =
    currentSecretSectionSnapshotSignature !== savedSecretSectionSnapshotSignature;

  React.useEffect(() => {
    if (hasDirtySecretFields) {
      return;
    }

    setSavedSecretSectionSnapshotSignature(currentSecretSectionSnapshotSignature);
    savedPledgorIdentifyCodeRef.current = getStringValue(pledgorIdentifyCode);
  }, [currentSecretSectionSnapshotSignature, hasDirtySecretFields, pledgorIdentifyCode]);

  const setValueIfChanged = React.useCallback(
    <TFieldName extends FieldPath<TGuaranteesRootSectionFormValues>>(
      name: TFieldName,
      value: FieldPathValue<TGuaranteesRootSectionFormValues, TFieldName>
    ) => {
      if (getComparableFormValue(getValues(name)) === getComparableFormValue(value)) {
        return;
      }

      setValue(name, value, { shouldDirty: true, shouldTouch: true });
    },
    [getValues, setValue]
  );
  const clearSelectedAccount = React.useCallback(() => {
    if (selectedAccountSecurityType) {
      delete draftAccountsBySecurityTypeRef.current[selectedAccountSecurityType];
    }

    setValue('account', null, { shouldDirty: true, shouldTouch: true });
  }, [selectedAccountSecurityType, setValue]);

  React.useEffect(() => {
    if (isSavedContractSelected) {
      return;
    }

    draftAccountsBySecurityTypeRef.current = {};
    setValueIfChanged('account', null);
    setValueIfChanged('coverageAccountIban', '');
    setValueIfChanged('depositAccountIban', '');
    setValueIfChanged('principalDebitAccount', null);
    setValueIfChanged('principalDebitAccountIban', '');
  }, [isSavedContractSelected, setValueIfChanged]);

  React.useEffect(() => {
    if (!selectedAccountSecurityType || !isSavedGuaranteeTypeSelected || !isSavedContractSelected) {
      return;
    }

    const loadKey = `${applicationLoadKey}:${selectedAccountSecurityType}`;

    if (loadedAccountsRef.current === loadKey) {
      return;
    }

    loadedAccountsRef.current = loadKey;

    void onAccountsLoad({
      isPrincipalDebitAccount: selectedAccountSecurityType === GuaranteeSecurityType.DEPOSIT_RIGHTS,
    });
  }, [
    applicationLoadKey,
    isSavedContractSelected,
    isSavedGuaranteeTypeSelected,
    onAccountsLoad,
    selectedAccountSecurityType,
  ]);

  React.useEffect(() => {
    if (
      !applicationLoadKey ||
      applicationLoadKey === 'new' ||
      !isSavedGuaranteeTypeSelected ||
      !isSavedContractSelected
    ) {
      return;
    }

    const loadKey = `${applicationLoadKey}:security-currencies`;

    if (loadedSecurityCurrenciesRef.current === loadKey) {
      return;
    }

    loadedSecurityCurrenciesRef.current = loadKey;
    void onSecurityCurrenciesLoad();
  }, [
    applicationLoadKey,
    isSavedContractSelected,
    isSavedGuaranteeTypeSelected,
    onSecurityCurrenciesLoad,
  ]);

  React.useEffect(() => {
    if (!applicationLoadKey || !isSavedGuaranteeTypeSelected || !isSavedContractSelected) {
      return;
    }

    const loadKey = `${applicationLoadKey}:principal-debit`;

    if (loadedPrincipalDebitAccountsRef.current === loadKey) {
      return;
    }

    loadedPrincipalDebitAccountsRef.current = loadKey;
    void onPrincipalDebitAccountsLoad();
  }, [
    applicationLoadKey,
    isSavedContractSelected,
    isSavedGuaranteeTypeSelected,
    onPrincipalDebitAccountsLoad,
  ]);

  React.useEffect(() => {
    if (previousSecurityTypeRef.current === undefined) {
      previousSecurityTypeRef.current = selectedAccountSecurityType;

      return;
    }

    if (previousSecurityTypeRef.current === selectedAccountSecurityType) {
      return;
    }

    const previousSecurityType = previousSecurityTypeRef.current;
    const previousAccount = selectedAccountOption;

    if (previousSecurityType && previousAccount?.iban && previousAccount.targetField) {
      draftAccountsBySecurityTypeRef.current[previousSecurityType] = previousAccount;
    }

    previousSecurityTypeRef.current = selectedAccountSecurityType;

    if (!selectedAccountSecurityType) {
      setValueIfChanged('coverageAccountIban', '');
      setValueIfChanged('depositAccountIban', '');
      setValueIfChanged('account', null);
      setValueIfChanged('securityCurrencies', []);

      return;
    }

    const draftAccount = draftAccountsBySecurityTypeRef.current[selectedAccountSecurityType] ?? null;
    const expectedTargetField = getGuaranteesAccountFieldBySecurityType(selectedAccountSecurityType);

    if (!expectedTargetField) {
      return;
    }

    if (draftAccount?.targetField === expectedTargetField) {
      setValueIfChanged('account', draftAccount);
      setValueIfChanged(
        GuaranteesAccountTargetField.CoverageAccountIban,
        expectedTargetField === GuaranteesAccountTargetField.CoverageAccountIban
          ? draftAccount.iban
          : ''
      );
      setValueIfChanged(
        GuaranteesAccountTargetField.DepositAccountIban,
        expectedTargetField === GuaranteesAccountTargetField.DepositAccountIban
          ? draftAccount.iban
          : ''
      );

      return;
    }

    if (selectedAccountSecurityType === GuaranteeSecurityType.COVERAGE_ACCOUNT) {
      setValueIfChanged('depositAccountIban', '');
    }

    if (selectedAccountSecurityType === GuaranteeSecurityType.DEPOSIT_RIGHTS) {
      setValueIfChanged('coverageAccountIban', '');
    }

    setValueIfChanged('account', null);
  }, [selectedAccountOption, selectedAccountSecurityType, setValueIfChanged]);

  React.useEffect(() => {
    if (isCounterpartyAccount || !selectedAccountOption) {
      return;
    }

    clearSelectedAccount();
  }, [clearSelectedAccount, isCounterpartyAccount, selectedAccountOption]);

  const handleAccountChange = (option: TAutocompleteOption | TAutocompleteOption[] | null) => {
    if (!isSavedContractSelected) {
      return;
    }

    const normalizedOption = Array.isArray(option) ? option[0] : option;
    const accountOption = findGuaranteesOptionByValue(
      filteredAccountOptions,
      normalizedOption?.value
    );

    if (!accountOption) {
      return;
    }

    if (selectedAccountOption && areSameAccountOptions(accountOption, selectedAccountOption)) {
      clearSelectedAccount();
      setValue(accountOption.targetField, '', { shouldDirty: true, shouldTouch: true });

      return;
    }

    if (selectedAccountSecurityType) {
      draftAccountsBySecurityTypeRef.current[selectedAccountSecurityType] = accountOption;
    }

    setValue('isCounterpartyAccount', true, { shouldDirty: true, shouldTouch: true });
    setValue('account', accountOption, { shouldDirty: true, shouldTouch: true });

    if (accountOption.targetField === GuaranteesAccountTargetField.DepositAccountIban) {
      setValue(GuaranteesAccountTargetField.CoverageAccountIban, '', {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    if (accountOption.targetField === GuaranteesAccountTargetField.CoverageAccountIban) {
      setValue(GuaranteesAccountTargetField.DepositAccountIban, '', {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    setValue(accountOption.targetField, accountOption.iban, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };
  const handleCounterpartyAccountChange = (isChecked: boolean) => {
    if (!isSavedContractSelected) {
      return;
    }

    if (isChecked || !selectedAccountOption) {
      return;
    }

    clearSelectedAccount();
  };
  const handleManualAccountIbanChange = () => {
    if (!isSavedContractSelected) {
      return;
    }

    if (!selectedAccountOption) {
      return;
    }

    clearSelectedAccount();
  };
  const handlePrincipalDebitAccountChange = (
    option: TAutocompleteOption | TAutocompleteOption[] | null
  ) => {
    if (!isSavedContractSelected) {
      return;
    }

    const normalizedOption = Array.isArray(option) ? option[0] : option;
    const accountOption = findGuaranteesOptionByValue(
      principalDebitAccountOptions,
      normalizedOption?.value
    );

    if (!accountOption) {
      return;
    }

    if (
      selectedPrincipalDebitAccountOption &&
      areSameAccountOptions(accountOption, selectedPrincipalDebitAccountOption)
    ) {
      setValue('principalDebitAccount', null, { shouldDirty: true, shouldTouch: true });
      setValue('principalDebitAccountIban', '', { shouldDirty: true, shouldTouch: true });

      return;
    }

    setValue('isCounterpartyPrincipalDebitAccount', true, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue('principalDebitAccount', accountOption, { shouldDirty: true, shouldTouch: true });
    setValue('principalDebitAccountIban', accountOption.iban, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };
  const handleCounterpartyPrincipalDebitAccountChange = (isChecked: boolean) => {
    if (!isSavedContractSelected) {
      return;
    }

    if (isChecked || !selectedPrincipalDebitAccountOption) {
      return;
    }

    setValue('principalDebitAccount', null, { shouldDirty: true, shouldTouch: true });
  };
  const handleManualPrincipalDebitAccountIbanChange = () => {
    if (!isSavedContractSelected) {
      return;
    }

    if (!selectedPrincipalDebitAccountOption) {
      return;
    }

    setValue('principalDebitAccount', null, { shouldDirty: true, shouldTouch: true });
  };
  const handleSecretSave = async () => {
    if (!isSavedContractSelected) {
      return;
    }

    const values = getValues();
    const secretErrors = validateGuaranteesSecretSectionForm(values);

    clearErrors(SECRET_FIELD_NAMES);

    if (Object.keys(secretErrors).length) {
      let shouldFocusFirstError = true;

      Object.entries(secretErrors).forEach(([fieldName, message]) => {
        if (!message) {
          return;
        }

        setError(
          fieldName as keyof TGuaranteesRootSectionFormValues,
          {
            type: 'manual',
            message,
          },
          { shouldFocus: shouldFocusFirstError }
        );
        shouldFocusFirstError = false;
      });

      return;
    }

    const isSaved = await onSecretSubmit(values, {
      hasPledgorIdentifyCodeChanged:
        getStringValue(values.pledgorIdentifyCode) !== savedPledgorIdentifyCodeRef.current,
    });
    const account = getValues().account;

    if (isSaved && account) {
      saveGuaranteesStoredAccountSelection(applicationLoadKey, account as TGuaranteesAccountOption);
    }

    if (isSaved) {
      savedPledgorIdentifyCodeRef.current = getStringValue(values.pledgorIdentifyCode);
      setSavedSecretSectionSnapshotSignature(
        getSecretSectionSnapshotSignature(buildSecretSectionSnapshot(getValues()))
      );

      SECRET_FIELD_NAMES.forEach((fieldName) => {
        resetField(fieldName, {
          defaultValue: getValues(fieldName),
        });
      });
    }
  };

  return {
    accountSectionProps: {
      accountOptions: filteredAccountOptions,
      principalDebitAccountOptions,
      securityCurrencyOptions: guaranteeCurrencyOptions,
      isLoadingAccounts,
      isLoadingPrincipalDebitAccounts,
      isLoadingSecurityCurrencies,
      isLoadingCurrencies,
      isSavedContractSelected,
      isSavedGuaranteeTypeSelected,
      isSavingAccount,
      isSecurityTypeSelected,
      isCounterpartyAccount,
      isCounterpartyPrincipalDebitAccount,
      hasChanges: hasAccountChanges,
      lockedUntilSavedHint: accountLockedHint,
      manualAccountFieldName,
      selectedAccount: selectedAccountOption,
      onAccountSelect: handleAccountChange,
      onCounterpartyAccountChange: handleCounterpartyAccountChange,
      onManualAccountIbanChange: handleManualAccountIbanChange,
      onPrincipalDebitAccountSelect: handlePrincipalDebitAccountChange,
      onCounterpartyPrincipalDebitAccountChange: handleCounterpartyPrincipalDebitAccountChange,
      onManualPrincipalDebitAccountIbanChange: handleManualPrincipalDebitAccountIbanChange,
      onSave: handleSecretSave,
    },
    hasAccountChanges,
    isSecurityTypeSelected,
  };
};
